/**
 * The Coeur d'Alene Resort Golf Course — Coeur d'Alene, ID.
 *
 * Played by the tournament in 2024 and 2025. Transcribed 2026-07-29 from
 * public scorecard databases, NOT from the physical card.
 *
 * Two things on this card look like transcription errors and are not:
 *
 *  - Hole 14 is a 218-yard par 3 off the tips and 137 off the front. That is
 *    the floating green, which is towed and re-anchored daily, so the printed
 *    number is the long setting rather than what you will actually play.
 *  - Holes 5 and 6 are back-to-back par 3s (148 and 169 off the Gold). Four
 *    separate sources print it that way, so it is the routing, not a column
 *    that slipped a row.
 *
 * The course is par 71 — 35 out, 36 in — with five par 3s and four par 5s.
 */

import { tees, type Seed } from '../card'

export const COEUR_DALENE_RESORT: Seed = {
  id: 'coeur-dalene-resort',
  name: "The Coeur d'Alene Resort Golf Course",
  par: 71,
  pars: [5, 4, 3, 4, 3, 3, 4, 4, 5, 4, 5, 3, 4, 3, 5, 4, 4, 4],
  si: [7, 3, 17, 5, 13, 15, 11, 9, 1, 6, 2, 14, 12, 10, 16, 8, 18, 4],
  yards: [
    [540, 479, 155, 322, 148, 169, 435, 437, 601, 469, 538, 249, 368, 218, 495, 429, 269, 482],
    [526, 436, 128, 307, 132, 163, 395, 420, 566, 438, 522, 208, 336, 175, 478, 417, 257, 451],
    [499, 377, 114, 256, 120, 155, 389, 399, 540, 406, 506, 180, 293, 147, 462, 401, 236, 434],
    [424, 327, 108, 249, 105, 136, 350, 370, 510, 351, 484, 148, 278, 137, 443, 391, 229, 396],
  ],
  tees: tees([
    ['gold', 'Gold', 71.8, 127, 6803],
    ['blue', 'Blue', 70.1, 122, 6355],
    ['tan', 'Tan', 68.2, 116, 5914],
    ['copper', 'Copper', 65.6, 110, 5436],
  ]),
  provenance:
    'Par is the strongest field here: four sources — bluegolf, golflink, ' +
    'mscorecard and 18birdies — print the identical par row, 35 out and 36 in ' +
    'for 71, and golflink reaches that from a completely different yardage set, ' +
    'so it is not one bad record copied four times. Stroke index is corroborated ' +
    'twice, by bluegolf and by 18birdies, which agree cell for cell: 7-3-17-5-13-' +
    '15-11-9-1 out, 6-2-14-12-10-16-8-18-4 in. That is a clean permutation of ' +
    '1-18, odd on the front and even on the back. It is weaker than it looks, ' +
    'though — both of those sources carry the SAME OLD YARDAGE EDITION (6497 off ' +
    'a tee called Black), so they may share one upstream record, and neither ' +
    'source that carries the current yardages publishes a stroke index at all. ' +
    'Treat the stroke index as two-source but single-lineage, and photograph it ' +
    'first. ' +
    'Yardages and ratings are from golflink, which is the only source matching ' +
    "the tee names and ratings the resort itself publishes today — Gold 71.8/127 " +
    'at 6803, Blue 70.1/122 at 6355, Tan 68.2/116 at 5914, Copper 65.6/110 at ' +
    '5436. All four nines sum to their printed Out/In/Total and every hole gets ' +
    'strictly shorter from Gold to Copper, so no column is transposed. The ' +
    'resort also has a Mauve tee at 4448 yards that golflink does not break out ' +
    'hole by hole; it is omitted rather than guessed. ' +
    'The older card that bluegolf, mscorecard and 18birdies still serve runs 306 ' +
    'yards shorter off the tips under different tee names (Black/Blue/White/Gold/' +
    'Silver). Par and stroke index are identical between the two editions and ' +
    'the routing is unchanged, which is why the stroke index is carried over — ' +
    'but that carry-over is an assumption, and it is the assumption most likely ' +
    'to be wrong. ' +
    'One published cell was visibly corrupt and is not used: 18birdies lists ' +
    "hole 10 off its shortest tee as 24 yards. " +
    'Worth a second look on the physical card: hole 4 is only 322 yards off the ' +
    'Gold yet carries stroke index 5, and hole 17 is a 269-yard par 4 carrying ' +
    'stroke index 18. Both are consistent across sources and both are plausible ' +
    'for a short, tight, water-defended resort course, but they are the two ' +
    'cells where a swap would be hardest to notice on screen.',
}
